/**
 * AnimalCodeDisplay Component
 *
 * Displays the current animal password code for teachers to show students.
 * Read-only display of the 5 animals in the correct order.
 */

import { memo } from 'react';
import {
  type AnimalCodeDisplayProps,
  getAnimalEmoji,
  getAnimalLabel,
} from './types';

/**
 * Size config for different display sizes.
 * Box dimensions are separate from emoji dimensions to prevent
 * Apple Color Emoji from extending beyond the visible box area.
 */
const SIZE_CONFIG = {
  sm: { box: 'w-14 h-14', emoji: 'text-xl', label: 'w-14' },
  md: { box: 'w-20 h-20', emoji: 'text-2xl', label: 'w-20' },
  lg: { box: 'w-28 h-28', emoji: 'text-3xl', label: 'w-28' },
} as const;

/**
 * AnimalCodeDisplay - Shows the animal code for teachers
 */
export const AnimalCodeDisplay = memo(function AnimalCodeDisplay({
  code,
  size = 'lg',
  className = '',
  testId = 'animal-code-display',
}: AnimalCodeDisplayProps) {
  const config = SIZE_CONFIG[size];

  return (
    <div
      className={`flex flex-col items-center gap-4 ${className}`}
      data-testid={testId}
    >
      <p className="text-lg text-neutral-600 font-medium text-center">
        Show this code to your students:
      </p>

      {/* Animal code boxes */}
      <div
        className="flex gap-3 p-6 rounded-2xl bg-gradient-to-b from-blue-50 to-white border-2 border-blue-200 shadow-lg"
        data-testid={`${testId}-boxes`}
      >
        {code.map((animal, index) => (
          <div
            key={`${animal}-${index}`}
            className={`
              ${config.box}
              flex items-center justify-center
              bg-white rounded-xl border-3 border-blue-300
              shadow-md overflow-hidden
            `}
            role="img"
            aria-label={`Position ${index + 1}: ${getAnimalLabel(animal)}`}
            data-testid={`${testId}-box-${index}`}
          >
            <span
              className={`${config.emoji} drop-shadow-sm leading-none`}
              style={{ display: 'inline-block', lineHeight: 1, padding: '8px' }}
            >
              {getAnimalEmoji(animal)}
            </span>
          </div>
        ))}
      </div>

      {/* Labels below each animal */}
      <div
        className="flex gap-3"
        data-testid={`${testId}-labels`}
      >
        {code.map((animal, index) => (
          <div
            key={`label-${animal}-${index}`}
            className={`
              ${config.label}
              text-center text-sm text-neutral-500 font-medium
            `}
          >
            {getAnimalLabel(animal)}
          </div>
        ))}
      </div>
    </div>
  );
});

AnimalCodeDisplay.displayName = 'AnimalCodeDisplay';

export default AnimalCodeDisplay;
